import React from 'react';
import { ArrowLeft, LucideIcon } from 'lucide-react';

export type ReportTone = 'neutral' | 'positive' | 'warning' | 'negative' | 'info';

export type ReportMetricItem = {
  label: string;
  value: React.ReactNode;
  helper?: string;
  tone?: ReportTone;
  icon?: LucideIcon;
};

export type ReportNavigationItem = {
  id: string;
  label: string;
  description: string;
  metric: string;
  group: string;
  icon: React.ElementType;
};

type ReportsOverviewProps = {
  title: string;
  description: string;
  period: string;
  metrics: ReportMetricItem[];
  onBack: () => void;
};

export const ReportsOverview: React.FC<ReportsOverviewProps> = ({
  title,
  description,
  period,
  metrics,
  onBack
}) => (
  <section className="rpt-overview">
    <div className="rpt-overview__intro">
      <button className="rpt-back" type="button" onClick={onBack}>
        <ArrowLeft size={16} />
        Voltar
      </button>
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <span className="rpt-period">Período {period}</span>
    </div>
    <ReportMetrics items={metrics} compact />
  </section>
);

type ReportsNavigationProps = {
  items: ReportNavigationItem[];
  activeId: string;
  onChange: (id: string) => void;
};

export const ReportsNavigation: React.FC<ReportsNavigationProps> = ({
  items,
  activeId,
  onChange
}) => {
  const groups = Array.from(new Set(items.map((item) => item.group)));

  return (
    <aside className="rpt-navigation" aria-label="Módulos de relatório">
      <div className="rpt-navigation__heading">
        <span className="rpt-eyebrow">Relatórios</span>
        <strong>Escolha uma visão</strong>
      </div>
      <label className="rpt-navigation__select">
        <span>Módulo atual</span>
        <select value={activeId} onChange={(event) => onChange(event.target.value)}>
          {groups.map((group) => (
            <optgroup label={group} key={group}>
              {items
                .filter((item) => item.group === group)
                .map((item) => (
                  <option value={item.id} key={item.id}>{item.label} · {item.metric}</option>
                ))}
            </optgroup>
          ))}
        </select>
      </label>
      {groups.map((group) => (
        <div className="rpt-navigation__group" key={group}>
          <span className="rpt-navigation__label">{group}</span>
          <div className="rpt-navigation__items">
            {items
              .filter((item) => item.group === group)
              .map(({ id, label, metric, icon: Icon }) => (
                <button
                  key={id}
                  className={activeId === id ? 'active' : ''}
                  onClick={() => onChange(id)}
                  type="button"
                  aria-current={activeId === id ? 'page' : undefined}
                >
                  <span className="rpt-navigation__icon"><Icon size={17} /></span>
                  <span className="rpt-navigation__name">{label}</span>
                  <span className="rpt-navigation__metric">{metric}</span>
                </button>
              ))}
          </div>
        </div>
      ))}
    </aside>
  );
};

type ReportPanelProps = {
  children: React.ReactNode;
};

export const ReportPanel: React.FC<ReportPanelProps> = ({ children }) => (
  <section className="rpt-panel">{children}</section>
);

type ReportHeaderProps = {
  title: string;
  description?: string;
  eyebrow?: string;
  actions?: React.ReactNode;
};

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  title,
  description,
  eyebrow = 'Visão do relatório',
  actions
}) => (
  <header className="rpt-header">
    <div className="rpt-header__copy">
      <span className="rpt-eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    {actions && <div className="rpt-header__actions">{actions}</div>}
  </header>
);

type ReportMetricsProps = {
  items: ReportMetricItem[];
  compact?: boolean;
};

export const ReportMetrics: React.FC<ReportMetricsProps> = ({ items, compact = false }) => (
  <div className={`rpt-metrics${compact ? ' rpt-metrics--compact' : ''}`}>
    {items.map(({ label, value, helper, tone = 'neutral', icon: Icon }) => (
      <div className={`rpt-metric rpt-metric--${tone}`} key={label}>
        {Icon && <span className="rpt-metric__icon"><Icon size={16} /></span>}
        <div className="rpt-metric__content">
          <span>{label}</span>
          <strong>{value}</strong>
          {helper && <small>{helper}</small>}
        </div>
      </div>
    ))}
  </div>
);

type ReportSectionProps = {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

export const ReportSection: React.FC<ReportSectionProps> = ({
  title,
  description,
  actions,
  children,
  className = ''
}) => (
  <section className={`rpt-section ${className}`.trim()}>
    <div className="rpt-section__header">
      <div>
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="rpt-section__actions">{actions}</div>}
    </div>
    <div className="rpt-section__body">{children}</div>
  </section>
);

type ReportEmptyStateProps = {
  title: string;
  description: string;
  icon: LucideIcon;
  action?: React.ReactNode;
};

export const ReportEmptyState: React.FC<ReportEmptyStateProps> = ({
  title,
  description,
  icon: Icon,
  action
}) => (
  <div className="rpt-empty">
    <span><Icon size={22} /></span>
    <strong>{title}</strong>
    <p>{description}</p>
    {action}
  </div>
);
